import * as THREE from 'three';
/**
 * buildingGeometry.js
 * 
 * This module contains utility functions for generating the 3D geometry of a building
 * using Three.js. It provides functions to create various building components like:
 * 
 * - Floor meshes and groups
 * - Building dimensions and measurements
 * - Default building parameters
 * 
 * 
 * This file works in conjunction with the BuildingContext to manage the state and
 * parameters of the building, and integrates with the Three.js rendering system
 * to create the visual representation of the building structure.
 */

/**
 * Default building width in voxel units
 * @constant
 */
export const DEFAULT_BUILDING_WIDTH = 42;

/**
 * Creates a group containing the floor meshes for each level of the building
 * @param {Object} params - Parameters for creating the level floors
 * @param {number} params.depth - The depth of the building
 * @param {number} params.levelNum - The number of levels in the building
 * @param {number} params.levelHeight - The height of each level
 * @param {number} params.groundFloorHeight - The height of the ground floor
 * @param {number} [params.width=DEFAULT_BUILDING_WIDTH] - The width of the building (default: 42)
 * @returns {THREE.Group} A group containing all the floor meshes
 */
export const createLevelFloorsGroup = (params) => {
  const { 
    depth, 
    levelNum, 
    levelHeight, 
    groundFloorHeight,
    width = DEFAULT_BUILDING_WIDTH 
  } = params;
  
  const group = new THREE.Group();
  const cubeSize = 1;

  // Floor material
  const floorMaterial = new THREE.MeshStandardMaterial({ color: 0x777777 });

  // Create geometry for floors (reused for all floor meshes)
  const geometry = new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize);

  // Ground floor
  for (let x = 0; x < width; x++) {
    for (let z = 0; z < depth; z++) {
      const floor = new THREE.Mesh(geometry, floorMaterial);
      floor.position.set(x * cubeSize, -1, z * cubeSize);
      floor.name = 'dynamic';
      group.add(floor);
    }
  }

  // Level floors
  for (let level = 0; level < levelNum; level++) {
    const yOffset = level === 0 
      ? groundFloorHeight * cubeSize 
      : groundFloorHeight * cubeSize + level * levelHeight * cubeSize;

    for (let x = 0; x < width; x++) {
      for (let z = 0; z < depth; z++) {
        const floor = new THREE.Mesh(geometry, floorMaterial);
        floor.position.set(x * cubeSize, yOffset, z * cubeSize);
        floor.name = 'dynamic';
        group.add(floor);
      }
    }
  }

  return group;
};

/**
 * Utility function to darken a color (RGB integer format)
 * @param {number} color - RGB color as an integer
 * @param {number} factor - Factor to darken by (0-1)
 * @returns {number} Darkened color as an integer
 */
export const darkenColor = (color, factor) => {
  const r = Math.floor((color >> 16) * factor);
  const g = Math.floor(((color >> 8) & 0x00ff) * factor);
  const b = Math.floor((color & 0x0000ff) * factor);
  return (r << 16) | (g << 8) | b;
};

/**
 * Utility function to lighten a hex color
 * @param {string} hex - Hex color code (with or without #)
 * @param {number} amount - Amount to lighten by (0-1)
 * @returns {string} Lightened hex color code
 */
export const lightenColor = (hex, amount) => {
  // Remove the # if it exists
  let hexValue = hex.replace('#', '');
  
  // Parse the RGB components
  let r = parseInt(hexValue.substring(0, 2), 16);
  let g = parseInt(hexValue.substring(2, 4), 16);
  let b = parseInt(hexValue.substring(4, 6), 16);
  
  // Lighten each component
  r = Math.min(255, Math.round(r + (255 - r) * amount));
  g = Math.min(255, Math.round(g + (255 - g) * amount));
  b = Math.min(255, Math.round(b + (255 - b) * amount));
  
  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

export default {
  createLevelFloorsGroup,
  darkenColor,
  lightenColor,
  DEFAULT_BUILDING_WIDTH
}; 